import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import { LayoutDashboard, LogOut, Plus, Minus, Trash2, Clock, MapPin, CalendarDays, Calendar, Pencil, X, Check, Repeat, Settings, Ban } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LOGO_URL = "https://customer-assets.emergentagent.com/job_pressingexpress/artifacts/d4qg5zqd_Capture%20d%E2%80%99%C3%A9cran%202025-01-06%20102414.png";

const DAY_NAMES = ["Lundi", "Mardi", "Mercredi", "Jeudi", "Vendredi", "Samedi", "Dimanche"];
const TIME_SLOTS = [
  { value: "matin", label: "Matin" },
  { value: "midi", label: "Midi" },
  { value: "soir", label: "Soir" }
];

function AdminSlots() {
  const navigate = useNavigate();
  const [slots, setSlots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedZone, setSelectedZone] = useState("lyon");
  const [showDialog, setShowDialog] = useState(false);
  const [showRecurringConfig, setShowRecurringConfig] = useState(false);
  const [zoneConfigs, setZoneConfigs] = useState([]);
  const [slotForm, setSlotForm] = useState({
    date: format(new Date(), "yyyy-MM-dd"),
    startTime: "09:00",
    endTime: "12:00",
    maxBookings: 5,
  });
  const [editingSlot, setEditingSlot] = useState(null);
  const [editForm, setEditForm] = useState({ startTime: "", endTime: "" });

  const getAuth = useCallback(() => {
    const auth = localStorage.getItem("adminAuth");
    if (!auth) {
      navigate("/admin");
      return null;
    }
    return { Authorization: `Basic ${auth}` };
  }, [navigate]);

  const fetchZoneConfigs = useCallback(async () => {
    const headers = getAuth();
    if (!headers) return;
    try {
      const response = await axios.get(`${API}/admin/zone-config`, { headers });
      setZoneConfigs(response.data);
    } catch (error) {
      console.error("Error fetching zone configs:", error);
    }
  }, [getAuth]);

  const updateZoneConfig = async (zoneId, allowedDays, dayTimeSlots) => {
    const headers = getAuth();
    if (!headers) return;
    try {
      // Format day_time_slots: "0:matin,soir|4:midi"
      const dayTimeSlotsStr = Object.entries(dayTimeSlots)
        .filter(([_, slots]) => slots.length > 0)
        .map(([day, slots]) => `${day}:${slots.join(",")}`)
        .join("|");
      
      await axios.put(
        `${API}/admin/zone-config/${zoneId}?allowed_days=${allowedDays.join(",")}&day_time_slots=${encodeURIComponent(dayTimeSlotsStr)}`,
        {},
        { headers }
      );
      toast.success("Configuration mise à jour");
      fetchZoneConfigs();
    } catch (error) {
      toast.error("Erreur lors de la mise à jour");
    }
  };

  const toggleDay = (zoneId, day) => {
    const config = zoneConfigs.find(c => c.zone_id === zoneId);
    if (!config) return;
    
    const currentDays = config.allowed_days || [];
    const currentDayTimeSlots = config.day_time_slots || {};
    let newDays;
    let newDayTimeSlots = { ...currentDayTimeSlots };
    
    if (currentDays.includes(day)) {
      // Supprimer le jour
      newDays = currentDays.filter(d => d !== day);
      delete newDayTimeSlots[String(day)];
    } else {
      // Ajouter le jour avec tous les créneaux par défaut
      newDays = [...currentDays, day].sort((a, b) => a - b);
      newDayTimeSlots[String(day)] = ["matin", "midi", "soir"];
    }
    updateZoneConfig(zoneId, newDays, newDayTimeSlots);
  };

  const toggleTimeSlotForDay = (zoneId, day, slot) => {
    const config = zoneConfigs.find(c => c.zone_id === zoneId);
    if (!config) return;
    
    const currentDayTimeSlots = config.day_time_slots || {};
    const dayKey = String(day);
    const currentSlots = currentDayTimeSlots[dayKey] || [];
    
    let newSlots;
    if (currentSlots.includes(slot)) {
      newSlots = currentSlots.filter(s => s !== slot);
    } else {
      newSlots = [...currentSlots, slot];
    }
    
    const newDayTimeSlots = {
      ...currentDayTimeSlots,
      [dayKey]: newSlots
    };
    
    updateZoneConfig(zoneId, config.allowed_days || [], newDayTimeSlots);
  };

  const fetchSlots = useCallback(async () => {
    const headers = getAuth();
    if (!headers) return;
    setLoading(true);
    try {
      const response = await axios.get(`${API}/admin/slots?zone_id=${selectedZone}`, { headers });
      setSlots(response.data);
    } catch (error) {
      if (error.response?.status === 401) {
        localStorage.removeItem("adminAuth");
        navigate("/admin");
      }
      toast.error("Erreur lors du chargement");
    } finally {
      setLoading(false);
    }
  }, [selectedZone, getAuth, navigate]);

  useEffect(() => { 
    fetchSlots(); 
    fetchZoneConfigs();
  }, [fetchSlots, fetchZoneConfigs]);

  const createSlot = async () => {
    const headers = getAuth();
    if (!headers) return;
    try {
      await axios.post(`${API}/admin/slots`, {
        zone_id: selectedZone,
        date: slotForm.date,
        start_time: slotForm.startTime,
        end_time: slotForm.endTime,
        max_bookings: slotForm.maxBookings,
      }, { headers });
      toast.success("Créneau créé");
      setShowDialog(false);
      fetchSlots();
    } catch (error) {
      toast.error("Erreur");
    }
  };

  const deleteSlot = async (slotId) => {
    const headers = getAuth();
    if (!headers) return;
    if (!window.confirm("Supprimer ?")) return;
    try {
      await axios.delete(`${API}/admin/slots/${slotId}`, { headers });
      toast.success("Supprimé");
      fetchSlots();
    } catch (error) {
      toast.error("Erreur");
    }
  };

  const addPlaces = async (slotId, places = 1) => {
    const headers = getAuth();
    if (!headers) return;
    try {
      await axios.post(`${API}/admin/slots/${slotId}/add-places?places=${places}`, {}, { headers });
      toast.success(`+${places} place(s) ajoutée(s)`);
      fetchSlots();
    } catch (error) {
      toast.error("Erreur");
    }
  };

  const removePlaces = async (slotId, places = 1) => {
    const headers = getAuth();
    if (!headers) return;
    try {
      await axios.post(`${API}/admin/slots/${slotId}/remove-places?places=${places}`, {}, { headers });
      toast.success(`-${places} place(s) retirée(s)`);
      fetchSlots();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur");
    }
  };

  const startEditSlot = (slot) => {
    setEditingSlot(slot.id);
    setEditForm({ startTime: slot.start_time, endTime: slot.end_time });
  };

  const cancelEdit = () => {
    setEditingSlot(null);
    setEditForm({ startTime: "", endTime: "" });
  };

  const saveSlotTime = async (slotId) => {
    const headers = getAuth();
    if (!headers) return;
    try {
      await axios.put(`${API}/admin/slots/${slotId}/time`, {
        start_time: editForm.startTime,
        end_time: editForm.endTime
      }, { headers });
      toast.success("Horaire modifié");
      setEditingSlot(null);
      fetchSlots();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur de modification");
    }
  };

  const markSlotFull = async (slotId) => {
    const headers = getAuth();
    if (!headers) return;
    try {
      await axios.post(`${API}/admin/slots/${slotId}/mark-full`, {}, { headers });
      toast.success("Créneau marqué comme complet");
      fetchSlots();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur");
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("adminAuth");
    navigate("/admin");
  };

  return (
    <div className="min-h-screen bg-slate-50 flex">
      <aside className="admin-sidebar hidden md:block">
        <div className="flex items-center justify-center mb-10">
          <img 
            src="https://customer-assets.emergentagent.com/job_pressingexpress/artifacts/d4qg5zqd_Capture%20d%E2%80%99%C3%A9cran%202025-01-06%20102414.png" 
            alt="Pressing Sakura" 
            className="h-16 w-auto bg-white rounded-lg p-1"
          />
        </div>
        <nav className="space-y-2">
          <div className="admin-nav-item" onClick={() => navigate("/admin/dashboard")} data-testid="nav-dashboard">
            <LayoutDashboard className="w-5 h-5" />
            <span>Tableau de bord</span>
          </div>
          <div className="admin-nav-item" onClick={() => navigate("/admin/planning")} data-testid="nav-planning">
            <CalendarDays className="w-5 h-5" />
            <span>Planning</span>
          </div>
          <div className="admin-nav-item active" data-testid="nav-slots">
            <Clock className="w-5 h-5" />
            <span>Créneaux</span>
          </div>
        </nav>
        <div className="absolute bottom-6 left-6">
          <button onClick={handleLogout} className="admin-nav-item text-slate-400 hover:text-white" data-testid="logout-btn">
            <LogOut className="w-5 h-5" />
            <span>Déconnexion</span>
          </button>
        </div>
      </aside>

      <main className="flex-1 p-6 md:p-10 overflow-auto pb-24 md:pb-10">
        {/* Mobile Navigation Tabs */}
        <div className="md:hidden mb-6">
          <div className="flex items-center justify-between mb-4">
            <h1 className="text-xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Créneaux
            </h1>
            <button onClick={handleLogout} className="p-2 text-slate-500">
              <LogOut className="w-5 h-5" />
            </button>
          </div>
          <div className="flex gap-2 overflow-x-auto pb-2">
            <Button size="sm" variant="outline" onClick={() => navigate("/admin/dashboard")} className="flex-shrink-0">
              <LayoutDashboard className="w-4 h-4 mr-1" />
              Dashboard
            </Button>
            <Button size="sm" variant="outline" onClick={() => navigate("/admin/planning")} className="flex-shrink-0">
              <Calendar className="w-4 h-4 mr-1" />
              Planning
            </Button>
            <Button size="sm" className="bg-primary text-white flex-shrink-0">
              <Clock className="w-4 h-4 mr-1" />
              Créneaux
            </Button>
          </div>
        </div>

        <div className="max-w-6xl mx-auto">
          <div className="hidden md:flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
            <div>
              <h1 className="text-2xl font-bold text-slate-900">Gestion des créneaux</h1>
              <p className="text-slate-500 mt-1">Créez et gérez vos créneaux</p>
            </div>
            <div className="flex gap-2">
              <select 
                value={selectedZone} 
                onChange={(e) => setSelectedZone(e.target.value)}
                className="px-4 py-2 border rounded-lg bg-white"
                data-testid="zone-selector"
              >
                <option value="lyon">Lyon</option>
                <option value="saint-rambert">Saint-Rambert</option>
                <option value="francheville">Francheville</option>
              </select>
            </div>
          </div>

          {/* Mobile zone selector */}
          <div className="md:hidden mb-4">
            <select 
              value={selectedZone} 
              onChange={(e) => setSelectedZone(e.target.value)}
              className="w-full px-4 py-2 border rounded-lg bg-white"
              data-testid="zone-selector-mobile"
            >
              <option value="lyon">Lyon</option>
              <option value="saint-rambert">Saint-Rambert</option>
              <option value="francheville">Francheville</option>
            </select>
          </div>

          <div className="flex gap-3 mb-8">
            <Button onClick={() => setShowDialog(true)} className="btn-primary" data-testid="add-slot-btn">
              <Plus className="w-4 h-4 mr-2" />
              Ajouter
            </Button>
            <Button 
              onClick={() => setShowRecurringConfig(!showRecurringConfig)} 
              variant="outline"
              className="text-purple-600 border-purple-300 hover:bg-purple-50"
            >
              <Settings className="w-4 h-4 mr-2" />
              Config récurrents
            </Button>
          </div>

          {/* Recurring Configuration Section */}
          {showRecurringConfig && (
            <Card className="mb-8 border-purple-200 bg-purple-50/30">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-purple-800">
                  <Repeat className="w-5 h-5" />
                  Configuration des jours et créneaux récurrents
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-slate-600 mb-4">
                  Configure les jours autorisés, puis les créneaux horaires <strong>par jour</strong>.
                </p>
                <div className="grid md:grid-cols-2 gap-6">
                  {zoneConfigs.map((config) => (
                    <div key={config.zone_id} className="bg-white p-4 rounded-lg border">
                      <h4 className="font-semibold text-lg mb-3 flex items-center gap-2">
                        <MapPin className="w-4 h-4 text-primary" />
                        {config.zone_name || config.zone_id}
                      </h4>
                      
                      <div className="mb-4">
                        <Label className="text-sm font-medium text-slate-700 mb-2 block">Jours autorisés</Label>
                        <div className="flex flex-wrap gap-2">
                          {DAY_NAMES.map((day, idx) => (
                            <button
                              key={idx}
                              onClick={() => toggleDay(config.zone_id, idx)}
                              className={`px-3 py-1.5 rounded-full text-sm font-medium ${
                                config.allowed_days?.includes(idx)
                                  ? "bg-purple-600 text-white"
                                  : "bg-gray-100 text-gray-600"
                              }`}
                            >
                              {day.slice(0,3)}
                            </button>
                          ))}
                        </div>
                      </div>
                      
                      {config.allowed_days?.length > 0 && (
                        <div>
                          <Label className="text-sm font-medium text-slate-700 mb-2 block">Créneaux par jour</Label>
                          <div className="space-y-2">
                            {config.allowed_days.map((dayIdx) => {
                              const daySlots = config.day_time_slots?.[String(dayIdx)] || [];
                              return (
                                <div key={dayIdx} className="flex items-center gap-2 p-2 bg-slate-50 rounded">
                                  <span className="font-medium text-sm w-16">{DAY_NAMES[dayIdx].slice(0,3)}</span>
                                  {TIME_SLOTS.map((slot) => (
                                    <button
                                      key={slot.value}
                                      onClick={() => toggleTimeSlotForDay(config.zone_id, dayIdx, slot.value)}
                                      className={`px-2 py-1 rounded text-xs font-medium ${
                                        daySlots.includes(slot.value)
                                          ? "bg-pink-500 text-white"
                                          : "bg-gray-200 text-gray-600"
                                      }`}
                                    >
                                      {slot.label}
                                    </button>
                                  ))}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {showDialog && (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
              <div className="bg-white rounded-xl p-6 w-full max-w-md mx-4">
                <h2 className="text-xl font-bold mb-4">Nouveau créneau</h2>
                <div className="space-y-4">
                  <div>
                    <Label>Date</Label>
                    <Input 
                      type="date" 
                      value={slotForm.date} 
                      onChange={(e) => setSlotForm(prev => ({ ...prev, date: e.target.value }))} 
                      className="mt-1" 
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Début</Label>
                      <Input 
                        type="time" 
                        value={slotForm.startTime} 
                        onChange={(e) => setSlotForm(prev => ({ ...prev, startTime: e.target.value }))} 
                        className="mt-1" 
                      />
                    </div>
                    <div>
                      <Label>Fin</Label>
                      <Input 
                        type="time" 
                        value={slotForm.endTime} 
                        onChange={(e) => setSlotForm(prev => ({ ...prev, endTime: e.target.value }))} 
                        className="mt-1" 
                      />
                    </div>
                  </div>
                  <div>
                    <Label>Places max</Label>
                    <Input 
                      type="number" 
                      min="1" 
                      value={slotForm.maxBookings} 
                      onChange={(e) => setSlotForm(prev => ({ ...prev, maxBookings: parseInt(e.target.value) || 5 }))} 
                      className="mt-1" 
                    />
                  </div>
                  <div className="flex gap-2 pt-4">
                    <Button onClick={() => setShowDialog(false)} variant="outline" className="flex-1">
                      Annuler
                    </Button>
                    <Button onClick={createSlot} className="flex-1 btn-primary" data-testid="create-slot-btn">
                      Créer
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          )}

          <Card>
            <CardHeader>
              <CardTitle>Créneaux - {selectedZone === "lyon" ? "Lyon" : selectedZone === "francheville" ? "Francheville" : "Saint-Rambert"}</CardTitle>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="text-center py-12">
                  <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full mx-auto"></div>
                </div>
              ) : slots.length === 0 ? (
                <div className="text-center py-12">
                  <CalendarDays className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-500">Aucun créneau</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left py-3 px-4">Date</th>
                        <th className="text-left py-3 px-4">Horaire</th>
                        <th className="text-left py-3 px-4">Réservations</th>
                        <th className="text-left py-3 px-4">Statut</th>
                        <th className="text-left py-3 px-4">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {slots.map((slot) => (
                        <tr key={slot.id} className="border-b">
                          <td className="py-3 px-4">
                            <CalendarDays className="w-4 h-4 inline mr-2 text-slate-400" />
                            {format(new Date(slot.date), "d MMM yyyy", { locale: fr })}
                          </td>
                          <td className="py-3 px-4">
                            {editingSlot === slot.id ? (
                              <div className="flex items-center gap-2">
                                <Input
                                  type="time"
                                  value={editForm.startTime}
                                  onChange={(e) => setEditForm({ ...editForm, startTime: e.target.value })}
                                  className="w-24 h-8 text-sm"
                                />
                                <span>-</span>
                                <Input
                                  type="time"
                                  value={editForm.endTime}
                                  onChange={(e) => setEditForm({ ...editForm, endTime: e.target.value })}
                                  className="w-24 h-8 text-sm"
                                />
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => saveSlotTime(slot.id)}
                                  className="h-7 w-7 text-green-600 hover:bg-green-50"
                                  title="Enregistrer"
                                >
                                  <Check className="w-4 h-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={cancelEdit}
                                  className="h-7 w-7 text-slate-500 hover:bg-slate-100"
                                  title="Annuler"
                                >
                                  <X className="w-4 h-4" />
                                </Button>
                              </div>
                            ) : (
                              <div className="flex items-center gap-2">
                                <Clock className="w-4 h-4 text-slate-400" />
                                {slot.start_time} - {slot.end_time}
                              </div>
                            )}
                          </td>
                          <td className="py-3 px-4">
                            <div className="flex items-center gap-2">
                              <Button 
                                variant="outline" 
                                size="sm"
                                onClick={() => removePlaces(slot.id, 1)} 
                                className="h-7 w-7 p-0 text-red-600 border-red-200 hover:bg-red-50"
                                title="Retirer une place"
                                disabled={slot.max_bookings <= slot.current_bookings || slot.max_bookings <= 1}
                              >
                                <Minus className="w-4 h-4" />
                              </Button>
                              <span className="min-w-[60px] text-center">{slot.current_bookings} / {slot.max_bookings}</span>
                              <Button 
                                variant="outline" 
                                size="sm"
                                onClick={() => addPlaces(slot.id, 1)} 
                                className="h-7 w-7 p-0 text-green-600 border-green-200 hover:bg-green-50"
                                title="Ajouter une place"
                              >
                                <Plus className="w-4 h-4" />
                              </Button>
                            </div>
                          </td>
                          <td className="py-3 px-4">
                            {slot.is_active ? (
                              slot.current_bookings >= slot.max_bookings ? (
                                <Badge className="bg-amber-100 text-amber-700">Complet</Badge>
                              ) : (
                                <Badge className="bg-green-100 text-green-700">Disponible</Badge>
                              )
                            ) : (
                              <Badge className="bg-slate-100 text-slate-700">Désactivé</Badge>
                            )}
                          </td>
                          <td className="py-3 px-4">
                            <div className="flex items-center gap-1">
                              {slot.current_bookings < slot.max_bookings && (
                                <Button 
                                  variant="ghost" 
                                  size="icon" 
                                  onClick={() => markSlotFull(slot.id)} 
                                  className="text-amber-500 hover:bg-amber-50"
                                  title="Marquer complet"
                                >
                                  <Ban className="w-4 h-4" />
                                </Button>
                              )}
                              <Button 
                                variant="ghost" 
                                size="icon" 
                                onClick={() => startEditSlot(slot)} 
                                className="text-blue-500 hover:bg-blue-50"
                                title="Modifier l'horaire"
                                disabled={editingSlot !== null}
                              >
                                <Pencil className="w-4 h-4" />
                              </Button>
                              <Button 
                                variant="ghost" 
                                size="icon" 
                                onClick={() => deleteSlot(slot.id)} 
                                className="text-red-500 hover:bg-red-50"
                                title="Supprimer"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        <div className="md:hidden fixed bottom-0 left-0 right-0 bg-white border-t p-4">
          <div className="flex justify-around">
            <button className="flex flex-col items-center text-slate-500" onClick={() => navigate("/admin/dashboard")}>
              <LayoutDashboard className="w-5 h-5" />
              <span className="text-xs">Dashboard</span>
            </button>
            <button className="flex flex-col items-center text-slate-500" onClick={() => navigate("/admin/planning")}>
              <Calendar className="w-5 h-5" />
              <span className="text-xs">Planning</span>
            </button>
            <button className="flex flex-col items-center text-primary">
              <Clock className="w-5 h-5" />
              <span className="text-xs">Créneaux</span>
            </button>
          </div>
        </div>
      </main>
    </div>
  );
}

export default AdminSlots;
